import React from "react";
import styled from "styled-components";
import { RichText, Text, types, Image } from "react-bricks/frontend";
import { color } from "styles/colours";
import { H2 } from "components/atoms/Heading";
import { P1 } from "components/atoms/Paragraph";
import { BetterButton } from "components/atoms/BetterButton";
import { useBreakpoints } from "hooks/useBreakpoints";
import { Link } from "components/atoms/Link";
import { verticalPadSideProp, getVerticalPadding, VerticalPadding } from "../utils/verticalPadding";

interface CallbackProps {
  buttonText: string;
  buttonLink: string;
  imgRight: number;
  imgBottom: number;
  titleColor: types.IColor;
  backgroundColor: types.IColor;
  borderColor: types.IColor;
  textColor: types.IColor;
  paddingVertical: VerticalPadding;
}

export const Callback: types.Brick<CallbackProps> = ({
  buttonText,
  buttonLink,
  imgRight,
  imgBottom,
  titleColor,
  backgroundColor,
  borderColor,
  textColor,
  paddingVertical,
}) => {
  const { isMobile } = useBreakpoints();

  return (
    <Wrapper paddingVertical={getVerticalPadding(paddingVertical)}>
      <CallbackContainer backgroundColor={backgroundColor.color} borderColor={borderColor.color}>
        <TextContainer>
          <Title color={titleColor?.color}>
            <Text
              propName="title"
              placeholder="Title"
              renderBlock={({ children }) => <>{children}</>}
            />
          </Title>
          <Content color={textColor?.color}>
            <RichText
              propName="content"
              placeholder="This is where the content for the callback lives..."
              renderBlock={({ children }) => <>{children}</>}
            />
          </Content>
          {!!buttonText && (
            <LinkWrapper>
              <Link href={buttonLink}>
                <BetterButton
                  variant="primary"
                  text={buttonText}
                  width={isMobile ? "100%" : "fit-content"}
                />
              </Link>
            </LinkWrapper>
          )}
        </TextContainer>
        <ImageContainer>
          <ImageWrapper right={imgRight} bottom={imgBottom}>
            <Image propName="image" alt={"image"} maxWidth={370} />
          </ImageWrapper>
        </ImageContainer>
      </CallbackContainer>
    </Wrapper>
  );
};

Callback.schema = {
  name: "callback",
  label: "Callback panel",
  category: "Callback Component",
  previewImageUrl: "/static/bricks/callback.png",
  // Define repeaters for child bricks
  repeaterItems: [],
  // Define default props
  getDefaultProps: () => ({
    backgroundColor: { color: color.S110 },
    borderColor: { color: color.S1100 },
    titleColor: { color: color.H100 },
    textColor: { color: color.B1100 },
    title: "Is YuLife available without insurance?",
    content:
      "Yes! You can still take steps to protect your employees’ wellbeing and inspire them to live their best lives. Our global app-only option provides all the benefits of the YuLife app, without the insurance.",
    buttonText: "Find out more",
    buttonLink: "/get-a-quote",
    image: { src: "/static/giraffes/YuLife-Yugi-finish-line.svg" },
    imgRight: 0,
    imgBottom: 0,
  }),
  // Define side edit props
  sideEditProps: [
    verticalPadSideProp,
    {
      name: "backgroundColor",
      label: "Background Color",
      type: types.SideEditPropType.Select,
      shouldRefreshStyles: true,
      selectOptions: {
        display: types.OptionsDisplay.Color,
        options: [
          { label: "Purple", value: { color: color.S310 } },
          { label: "Dark Purple", value: { color: color.S3100 } },
          { label: "Green", value: { color: color.S110 } },
        ],
      },
    },

    {
      name: "borderColor",
      label: "Border Color",
      type: types.SideEditPropType.Select,
      shouldRefreshStyles: true,
      selectOptions: {
        display: types.OptionsDisplay.Color,
        options: [
          { label: "Purple", value: { color: color.S3100 } },
          { label: "Green", value: { color: color.S1100 } },
        ],
      },
    },
    {
      name: "titleColor",
      label: "Title Color",
      type: types.SideEditPropType.Select,
      shouldRefreshStyles: true,
      selectOptions: {
        display: types.OptionsDisplay.Color,
        options: [
          { label: "Purple", value: { color: color.S3100 } },
          { label: "Ink", value: { color: color.H100 } },
          { label: "White", value: { color: color.white } },
        ],
      },
    },
    {
      name: "textColor",
      label: "Text Color",
      type: types.SideEditPropType.Select,
      shouldRefreshStyles: true,
      selectOptions: {
        display: types.OptionsDisplay.Color,
        options: [
          { label: "Stone", value: { color: color.B1100 } },
          { label: "White", value: { color: color.white } },
        ],
      },
    },
    {
      name: "buttonText",
      label: "Button Text",
      type: types.SideEditPropType.Text,
    },
    {
      name: "buttonLink",
      label: "Button Link",
      type: types.SideEditPropType.Text,
      show: (props) => !!props.buttonText,
    },
    {
      name: "imgRight",
      label: "Image horizontal offset",
      type: types.SideEditPropType.Range,
      shouldRefreshStyles: true,
      rangeOptions: {
        min: -10,
        max: 10,
        step: 1,
      },
    },
    {
      name: "imgBottom",
      label: "Image vertical Offset",
      type: types.SideEditPropType.Range,
      shouldRefreshStyles: true,
      rangeOptions: {
        min: -10,
        max: 10,
        step: 1,
      },
    },
  ],
};

const Wrapper = styled.div<{ paddingVertical: string }>`
  margin: 0 auto;
  padding: ${(p) => p.paddingVertical} 2rem;
  display: flex;
  justify-content: center;

  @media only screen and (max-width: 23.75rem) {
    padding: 0 0.5rem;
  }
`;

const CallbackContainer = styled.div<{
  backgroundColor: string;
  borderColor: string;
}>`
  background-color: ${(p) => p.backgroundColor};
  border: ${(p) => `1px ${p.borderColor} solid`};
  border-radius: 0.5rem;
  display: flex;
  justify-content: space-between;
  gap: 1rem;
  min-height: 24rem;
  max-width: 58rem;

  @media only screen and (max-width: 50rem) {
    width: 100%;
  }
`;

const TextContainer = styled.div`
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  max-width: 31.5rem;
  margin: 2.5rem 0 2.5rem 2.5rem;

  @media only screen and (max-width: 50rem) {
    width: 100%;
    max-width: none;
    margin: 2.5rem;
  }
`;

const Title = styled(H2)<{
  color: string;
}>`
color: ${(p) => p.color}
font-size: 2.5rem;
width: 100%;
`;

const Content = styled(P1)<{ color: string }>`
  width: 100%;
  margin-bottom: 2rem;
  color: ${(p) => p.color};
`;

const ImageContainer = styled.div`
  min-width: 23rem;
  position: relative;

  @media only screen and (max-width: 64rem) {
    max-width: 16rem;
  }

  @media only screen and (max-width: 50rem) {
    display: none;
  }
`;

const ImageWrapper = styled.div<{
  right: number;
  bottom: number;
}>`
  position: relative;
  right: ${(p) => `${p.right}rem`};
  bottom: ${(p) => `${p.bottom}rem`};
  z-index: 1;
  width: 100%;
  img {
    max-width: 100%;
  }
`;

const LinkWrapper = styled.div`
  width: 100%;
`;
